import {NextPage} from "next";
import {wrapper} from "../../app/store";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {CartView} from "../../components/cart/CartView";
import React, {useEffect} from "react";
import {fetchAndUpdateCartIfNeeded} from "../../redux/cart/cartSlice";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import Bugsnag from "@bugsnag/js";
import {StoreJson, StoreModel} from "../../models/store";
import {Container} from "../../components/containers/Container";
import {useRouter} from "next/router";
import Head from "next/head";
import {selectStore} from "../../redux/store/storeSlice";
import {MetaTag} from "../../models/metaTag";

interface CartProps {
  id?: string
}

const Cart: NextPage<CartProps> = ({ id }) => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const store = useAppSelector(selectStore)

  useEffect(() => {
    if (id) {
      dispatch(fetchAndUpdateCartIfNeeded(id))
    }
  }, [id, dispatch])

  return <>
    <Container className="max-h-screen sticky">
      <CartView isMenu={false} onClose={() => {
        if (id) {
          router.push(`/store/${id}`)
        } else {
          router.push(`/store-feed`)
        }
      }} />
    </Container>
  </>
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-available-stores`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })
  const stores: StoreJson[] = (await res?.json()) ?? []

  const paths = stores.map((store) => ({
    params: { id: store._id },
  }))

  return { paths, fallback: true }
}

const createMetaTags = (params: any, storeJson?: StoreJson): MetaTag[] => {
  const store = StoreModel.fromJson(storeJson)

  const metaTitle = `Your Items${store ? ` | ${store.header?.title}` : ''}`
  const metaUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN_URL}/cart/${params.id ?? store?.id}`

  return [
    {
      property: 'og:title',
      content: metaTitle
    },
    {
      property: 'og:url',
      content: metaUrl
    },
    {
      property: 'twitter:title',
      content: metaTitle
    },
    {
      property: 'twitter:card',
      content: 'summary_large_image'
    },
  ]
}

export const getStaticProps = wrapper.getStaticProps(store => async ({ locale, params }: any) => {
  const translations = await serverSideTranslations(locale, ['common', 'landing', 'auth', 'location', 'store', 'payment', 'checkout', 'order'])
    .catch(e => {
      Bugsnag.notify(e)
    })

  if (params && params.id) {
    return {
      props: {
        id: params.id,
        metaTags: createMetaTags(params),
        ...translations
      }
    }
  }

  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-store/${params.id}`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })

  const storeJson: StoreJson | undefined = await res?.json()


  return {
    props: {
      id: storeJson?._id,
      metaTags: createMetaTags(params, storeJson),
      ...translations,
    }
  }
});

export default Cart